import {inject, injectable} from "tsyringe";
import {BackendClient} from "../../connectivity/backend-client";
import {RestorableRow, RestorableRowDto, toRestorableRow} from "../restorable-row.mapper";

/**
 * NOT under `/api/v1/query/` on purpose: that group ends in a `/{entity}` catch-all
 * (`index.php:188-192`), so a sibling path there would be swallowed by it. This route carries
 * its own authorization check (`restore.entity`, the same right as the command).
 */
export const RESTORABLE_ENDPOINT = `/api/v1/restorable-entities`;

/**
 * What the endpoint answers INSIDE the house `{data: …}` wrapper — `BackendClient.request`
 * peels that wrapper off (`backend-client.ts:137`) and hands through nothing but `data`, which
 * is exactly why `failedTypes` lives next to `rows` and not beside `success`: anything outside
 * `data` would be lost on the way.
 */
export interface RestorableStockDto {
    readonly rows: RestorableRowDto[];
    /** Whitelist keys (`issue_type`, …) whose query failed server-side; their rows are simply absent. */
    readonly failedTypes: string[];
}

/** The same after parsing — what the dialog holds. */
export interface RestorableStock {
    readonly rows: RestorableRow[];
    readonly failedTypes: string[];
}

/**
 * Reads every soft-deleted, restorable row in ONE request (O2D-PRD-101).
 *
 * Written against `BackendClient` directly rather than `EntityManager.query`, and that is the
 * whole point of this class: `EntityManager.query` hardcodes `/api/v1/query/<kebab-entity-name>`
 * and can therefore not address a route outside that group at all. Going through the entity
 * machinery would also drag the rest of it along — a registered `Entity` class and a `@transformer`
 * per restorable type — which is precisely the coupling this endpoint exists to remove. The
 * pattern is the one `backend-linkview.repository.ts:494-499` already uses: post, map, done.
 *
 * NO try/catch here. A 403 (no `restore.entity` right) must reach the dialog as an `HttpError`
 * so it can SAY so; swallowing it would render an empty table, which reads as "the trash is
 * empty" — the opposite of the truth, and the exact failure the old per-type loader had.
 */
@injectable()
export class RestorableRepository {

    public constructor(
        @inject(BackendClient) private readonly _backendClient: BackendClient,
    ) {
    }

    public async findAll(): Promise<RestorableStock> {
        const payload = await this._backendClient.post<unknown>(RESTORABLE_ENDPOINT, false, {});
        const stock = RestorableRepository.unwrap(payload);

        return {
            rows: stock.rows.map(toRestorableRow),
            failedTypes: stock.failedTypes,
        };
    }

    /**
     * The ONE place that knows the envelope — and it knows exactly ONE.
     *
     * `{rows, failedTypes}` is what the server sends, nothing else is accepted. This used to
     * tolerate a bare array as well; that leniency is gone on purpose, because a shape check
     * that forgives is a shape check that hides — the date bug of 2026-09-20 sat unnoticed
     * behind exactly this kind of "whatever comes, make something of it". A wrong shape now
     * throws, and the dialog shows its load error instead of an empty, plausible-looking trash.
     */
    private static unwrap(payload: unknown): RestorableStockDto {
        if (
            typeof payload !== 'object' || payload === null
            || !Array.isArray((payload as {rows?: unknown}).rows)
            || !Array.isArray((payload as {failedTypes?: unknown}).failedTypes)
        ) {
            throw new Error(
                `[RestorableRepository] unexpected response shape from ${RESTORABLE_ENDPOINT}: `
                + `expected {rows: [...], failedTypes: [...]}, got ${describeShape(payload)}`,
            );
        }

        return payload as RestorableStockDto;
    }
}

function describeShape(payload: unknown): string {
    if (Array.isArray(payload)) {
        return 'an array';
    }
    if (payload === null) {
        return 'null';
    }
    if (typeof payload === 'object') {
        return `an object with keys [${Object.keys(payload).join(', ')}]`;
    }

    return typeof payload;
}
